import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { CoverImage } from '@/components/CoverImage';
import { Star } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import type { BookDetail } from '@/lib/wrappedUtils';

interface WrappedBookCardProps {
  book: BookDetail;
  title: string;
  subtitle?: string;
  icon?: React.ReactNode;
  showRating?: boolean;
  showCommunityRating?: boolean;
  showDate?: boolean;
}

export function WrappedBookCard({
  book,
  title,
  subtitle,
  icon,
  showRating = false,
  showCommunityRating = false,
  showDate = false
}: WrappedBookCardProps) {
  const navigate = useNavigate();
  
  return (
    <Card className="card-elevated overflow-hidden">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-lg">
          {icon}
          {title}
        </CardTitle>
        {subtitle && (
          <p className="text-sm text-muted-foreground">{subtitle}</p>
        )}
      </CardHeader>
      <CardContent className="flex gap-4">
        <div className="flex-shrink-0">
          <CoverImage
            bookId={book.id}
            title={book.title}
            author={book.authors?.[0]}
            coverUrl={book.coverUrl}
            size="md"
            className="w-24 h-36 object-cover rounded-lg shadow-lg"
          />
        </div>
        <div className="flex-1 space-y-2">
          <div>
            <h3 className="font-semibold text-base line-clamp-2 leading-tight">
              {book.title}
            </h3>
            <p className="text-sm text-muted-foreground line-clamp-1">
              by {book.authors.map((author, index) => (
                <React.Fragment key={author}>
                  <a 
                    href={`/author/${encodeURIComponent(author)}`}
                    className="hover:text-primary hover:underline transition-colors"
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      navigate(`/author/${encodeURIComponent(author)}`);
                    }}
                  >
                    {author}
                  </a>
                  {index < book.authors.length - 1 && ', '}
                </React.Fragment>
              ))}
            </p>
          </div>
          
          <div className="flex flex-wrap gap-2">
            {book.pageCount && (
              <Badge variant="secondary" className="text-xs">
                {book.pageCount} pages
              </Badge>
            )}
            
            {showRating && book.rating && (
              <Badge variant="outline" className="text-xs flex items-center gap-1">
                <Star className="w-3 h-3 fill-star text-star" />
                {book.rating.toFixed(1)}
              </Badge>
            )}
            
            {showCommunityRating && book.averageRating && (
              <Badge variant="outline" className="text-xs flex items-center gap-1">
                <Star className="w-3 h-3 fill-amber-400 text-amber-400" />
                {book.averageRating.toFixed(1)} ({book.ratingCount} ratings)
              </Badge>
            )}
            
            {showDate && book.finishedAt && (
              <Badge variant="secondary" className="text-xs">
                {format(parseISO(book.finishedAt), 'MMM d, yyyy')}
              </Badge>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
